/**
 * Composant de protection de route pour les responsables
 * 
 * Protège les routes nécessitant le rôle "responsable"
 * - Affiche "Chargement..." pendant la vérification de session
 * - Redirige vers /auth/login si non authentifié
 * - Redirige vers /portail-medecin si authentifié mais pas responsable
 * - Affiche les enfants si authentifié et responsable
 */

import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { apiFetch } from '../apiMedecins.js';
import { sanitizeResponsibleBack } from '../responsableBack.js';
import { scrollToPageTop } from './ScrollToTop.jsx';

function hasLockedSupervisorBridgeContext(location = window.location) {
  const isTransversalResponsibleScreen =
    location.pathname === '/responsable/equipe' ||
    location.pathname === '/responsable/vie-service';

  function isValidSupervisorBridgeBack(safeBack, depth = 0) {
    if (depth > 8) return false;
    const backUrl = new URL(safeBack, window.location.origin);

    if (backUrl.pathname === '/multisecteur/responsable/index.html') {
      const secteur = backUrl.searchParams.get('secteur') || '';
      const supervisorBack = sanitizeResponsibleBack(backUrl.searchParams.get('back') || '');
      const supervisorBackUrl = supervisorBack
        ? new URL(supervisorBack, window.location.origin)
        : null;
      return (
        /^sect-[a-z0-9-]+$/.test(secteur) &&
        (
          backUrl.searchParams.get('lockSecteur') === '1' ||
          (
            backUrl.searchParams.get('popup') === '1' &&
            /^\/(multisecteur\/)?superviseur(\/|$)/.test(supervisorBackUrl?.pathname || '')
          )
        )
      );
    }

    if (backUrl.pathname === '/responsable/index.html') {
      const secteur = backUrl.searchParams.get('secteur') || '';
      const supervisorBack = sanitizeResponsibleBack(backUrl.searchParams.get('back') || '');
      if (!supervisorBack) return false;
      const supervisorBackUrl = new URL(supervisorBack, window.location.origin);
      return (
        /^sect-[a-z0-9-]+$/.test(secteur) &&
        backUrl.searchParams.get('popup') === '1' &&
        /^\/(multisecteur\/)?superviseur(\/|$)/.test(supervisorBackUrl.pathname)
      );
    }

    if (!/^\/responsable(\/|$)/.test(backUrl.pathname)) return false;
    if (backUrl.pathname === '/responsable/login.html') return false;
    if (backUrl.searchParams.get('lockSecteur') !== '1') return false;

    const nestedBack = sanitizeResponsibleBack(backUrl.searchParams.get('back') || '');
    return Boolean(nestedBack) && isValidSupervisorBridgeBack(nestedBack, depth + 1);

    return false;
  }

  const params = new URLSearchParams(location.search);
  if (params.get('lockSecteur') !== '1') return false;

  const rawBack = params.get('back') || '';
  const safeBack = sanitizeResponsibleBack(rawBack);
  if (!safeBack) return false;

  // Les deux espaces transversaux sont ouverts volontairement depuis le
  // tableau de bord supervision general, sans contexte de secteur.
  if (isTransversalResponsibleScreen) {
    const backUrl = new URL(safeBack, window.location.origin);
    return /^\/(multisecteur\/)?superviseur\/index\.html$/.test(backUrl.pathname);
  }

  return isValidSupervisorBridgeBack(safeBack);
}

function isResponsibleRole(role) {
  const roleNorm = String(role || '').trim();
  if (roleNorm === 'responsable' || roleNorm === 'responsable_thoracique' || roleNorm.startsWith('responsable_')) {
    return true;
  }
  if (roleNorm === 'supervision') {
    return hasLockedSupervisorBridgeContext();
  }
  return false;
}

export default function ProtectedRouteResponsable({ children }) {
  const { loading, isAuthenticated, user } = useAuth();
  const isUcaRoster = window.location.pathname === '/responsable/gardes-liste-medecins'
    && new URLSearchParams(window.location.search).get('type')?.toUpperCase() === 'UCA';
  const checkPermission = isUcaRoster && user?.role === 'medecin';
  const [permission, setPermission] = useState(null);
  useEffect(() => {
    let active = true;
    setPermission(null);
    if (!loading && isAuthenticated && checkPermission) {
      apiFetch('/api/v2/planning-uca/access', { cache: 'no-store' })
        .then(response => { if (active) setPermission(response.ok); })
        .catch(() => { if (active) setPermission(false); });
    }
    return () => { active = false; };
  }, [loading, isAuthenticated, checkPermission, user?.id]);
  const isResponsable = isResponsibleRole(user?.role) || (checkPermission && permission === true);

  useLayoutEffect(() => {
    if (loading || !isAuthenticated || !isResponsable) return undefined;

    scrollToPageTop();
    const timeoutIds = [60, 180, 360, 700].map((delay) => window.setTimeout(scrollToPageTop, delay));

    return () => {
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [loading, isAuthenticated, isResponsable]);

  // Pendant le chargement, afficher un état de chargement
  if (loading || (isAuthenticated && checkPermission && permission === null)) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Inter, sans-serif',
          color: '#4b5563',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '16px', marginBottom: '8px' }}>Chargement…</div>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>Vérification de la session…</div>
        </div>
      </div>
    );
  }

  // Si non authentifié OU si authentifié mais pas responsable (global ou sectoriel), rediriger vers /auth/login
  if (!isAuthenticated || !isResponsable) {
    console.log('⚠️ [PROTECTED ROUTE RESPONSABLE] Accès refusé - redirection vers /auth/login', {
      isAuthenticated,
      username: user?.username,
      role: user?.role,
    });
    // Rediriger vers la page de connexion avec indication responsable
    // L'utilisateur pourra se connecter avec un compte responsable
    window.location.replace("/responsable/login.html");
    return null;
  }
  
  console.log('✅ [PROTECTED ROUTE RESPONSABLE] Accès autorisé:', { username: user?.username, role: user?.role });

  // Si authentifié et responsable, afficher les enfants
  return children;
}
